
import { Component, ChangeDetectionStrategy, inject, signal, OnInit } from '@angular/core';
import { CommonModule, DatePipe, JsonPipe } from '@angular/common';
import { DiagnosticsService, DiagnosticIssue } from '../../../services/diagnostics.service';
import { SmokeTestWidgetComponent } from '../../smoke-test-widget/smoke-test-widget.component';
import { FormsModule } from '@angular/forms';
import { PlatformService, StoreSummary } from '../../../services/platform.service';
import { LogicVerificationComponent } from './logic-verification/logic-verification.component';

type Tab = 'health' | 'integrity' | 'security' | 'alerts' | 'logic' | 'simulator';

@Component({
  selector: 'app-system-diagnostics',
  standalone: true,
  imports: [CommonModule, SmokeTestWidgetComponent, DatePipe, JsonPipe, FormsModule, LogicVerificationComponent],
  templateUrl: './system-diagnostics.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SystemDiagnosticsComponent implements OnInit {
  private diagnosticsService = inject(DiagnosticsService);
  private platformService = inject(PlatformService);

  activeTab = signal<Tab>('health');
  
  // Data Signals
  integrityIssues = signal<DiagnosticIssue[]>([]);
  securityRisks = signal<DiagnosticIssue[]>([]);
  recentAlerts = signal<any[]>([]);
  stores = signal<StoreSummary[]>([]);
  
  // Simulator State
  simStoreId = signal('');
  simAmount = signal(1000);
  simRefId = signal('chrg_test_' + Math.random().toString(36).substring(7));
  simResult = signal<any>(null);
  
  isSimulating = signal(false);
  isLoading = signal(false);
  lastScan = signal<Date | null>(null);

  // Overall Score (Simple calculation based on issue count)
  healthScore = signal('A');
  scoreColor = signal('text-emerald-500');

  ngOnInit() {
    this.runScan('health'); 
    this.loadStores();
  }

  async loadStores() {
      try {
          const data = await this.platformService.getAllStores();
          this.stores.set(data);
      } catch (e) {
          console.error("Failed to load stores for simulator", e);
      }
  }

  setTab(tab: Tab) {
    this.activeTab.set(tab);
    if (tab !== 'health' && tab !== 'logic' && tab !== 'simulator') {
        this.runScan(tab);
    }
  }

  async runScan(type: Tab | 'all') {
    this.isLoading.set(true);
    try {
        if (type === 'integrity' || type === 'all') {
            this.integrityIssues.set(await this.diagnosticsService.runDataIntegrityScan());
        }
        if (type === 'security' || type === 'all') {
            this.securityRisks.set(await this.diagnosticsService.runSecurityAudit());
        }
        if (type === 'alerts' || type === 'all') {
            this.recentAlerts.set(await this.diagnosticsService.getRecentAlerts());
        }
        
        this.calculateScore();
        this.lastScan.set(new Date());
    } catch (e) {
        console.error("Diagnostic scan failed", e);
    } finally {
        this.isLoading.set(false);
    }
  }
  
  async runSimulatePayment() {
      if (!this.simStoreId()) return;
      this.isSimulating.set(true);
      this.simResult.set(null);
      try {
          const result = await this.diagnosticsService.simulatePayment(this.simStoreId(), this.simAmount(), this.simRefId());
          this.simResult.set(result);
          // Auto-rotate Ref ID for next test
          this.simRefId.set('chrg_test_' + Math.random().toString(36).substring(7));
      } catch (e: any) {
          this.simResult.set({ status: 'ERROR', message: e.message });
      } finally {
          this.isSimulating.set(false);
      }
  }

  calculateScore() {
      const issueCount = this.integrityIssues().length + this.securityRisks().length;
      if (issueCount === 0) { this.healthScore.set('A'); this.scoreColor.set('text-emerald-500'); }
      else if (issueCount <= 2) { this.healthScore.set('B'); this.scoreColor.set('text-blue-500'); }
      else if (issueCount <= 5) { this.healthScore.set('C'); this.scoreColor.set('text-yellow-500'); }
      else { this.healthScore.set('D'); this.healthScore.set('D'); this.scoreColor.set('text-red-500'); }
  }

  getSeverityClass(severity: string) {
      switch(severity) {
          case 'CRITICAL': return 'bg-red-100 text-red-800 border-red-200';
          case 'HIGH': return 'bg-orange-100 text-orange-800 border-orange-200';
          case 'MEDIUM': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
          default: return 'bg-blue-100 text-blue-800 border-blue-200';
      }
  }
}
